import React, { useState } from 'react';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { FileText, ChevronRight, Smartphone, Star, Share } from 'lucide-react';
import { shareFile } from '../../../utils/shareFile';
import { notify } from '../../../utils/notify';
import { Card, IconTile, Section } from './primitives';

/*
 * Documents driven by data:
 *   groups: [{ id, title, items: [DocItem] }]
 *   DocItem: { title, url, subtitle? }            -> PDF, opens in a new tab (tile icon says PDF; no trailing icon)
 *          | { title, action, subtitle? }         -> in-app guide, handled by onAction(action) (phone tile + chevron)
 * Items with neither a usable url nor an action are hidden rather than shown as dead links.
 * Readers can pin documents (star); pins live on the device under `storageKey` (and in the profile when signed in)
 * and gather in a "Pinned" card at the top, like favourites on Contacts.
 */

export const isAvailable = (doc) => Boolean(doc.action || (doc.url && doc.url !== '#'));
const keyOf = (doc) => doc.action || doc.url;

const isPdf = (doc) => !doc.action && doc.url && /\.pdf(\?|$)/i.test(doc.url);

const ShareButton = ({ url, title, className = '' }) => {
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBusy(true);
    const result = await shareFile(url, title);
    setBusy(false);
    if (result === 'downloaded') notify.success('Downloaded');
  };
  return (
    <button onClick={go} disabled={busy} aria-label={`Share ${title}`} className={`px-3 flex items-center text-gray-300 dark:text-gray-600 hover:text-gray-500 disabled:opacity-50 ${className}`}>
      <Share size={18} className={busy ? 'animate-pulse' : ''} />
    </button>
  );
};

export const DocumentRow = ({ doc, accent, onAction, pinned, onTogglePin }) => {
  const inApp = Boolean(doc.action);
  const body = (
    <>
      <IconTile icon={inApp ? Smartphone : FileText} accent={accent} />
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-medium text-gray-900 dark:text-white leading-snug">{doc.title}</div>
        {doc.subtitle && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{doc.subtitle}</div>}
      </div>
      {inApp && <ChevronRight size={18} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />}
    </>
  );
  const cls = 'flex-1 min-w-0 flex items-center gap-3 pl-4 py-3 text-left';
  return (
    <div className="flex items-stretch">
      {inApp ? (
        <button onClick={() => { onAction?.(doc.action); }} className={cls}>{body}</button>
      ) : (
        <a href={doc.url} target="_blank" rel="noopener noreferrer" onClick={() => {}} className={cls}>{body}</a>
      )}
      {isPdf(doc) && <ShareButton url={doc.url} title={doc.title} />}
      {onTogglePin ? (
        <button
          onClick={() => { onTogglePin(keyOf(doc)); }}
          aria-label={pinned ? 'Unpin' : 'Pin'}
          className="px-4 flex items-center text-gray-300 dark:text-gray-600"
        >
          <Star size={18} className={pinned ? 'fill-amber-400 text-amber-400' : ''} />
        </button>
      ) : (
        <div className="w-4" />
      )}
    </div>
  );
};

const DocumentList = ({ groups, accent, onAction, storageKey }) => {
  const [pinned, setPinned] = useLocalStorage(storageKey || 'pinned-docs:none', []);

  const togglePin = storageKey
    ? (key) => setPinned((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
    : null;

  const allDocs = groups.flatMap((g) => g.items).filter(isAvailable);
  const pinnedDocs = pinned.map((k) => allDocs.find((d) => keyOf(d) === k)).filter(Boolean);

  return (
    <div className="space-y-5">
      {pinnedDocs.length > 0 && (
        <Section title="Pinned">
          <Card>
            {pinnedDocs.map((doc) => (
              <DocumentRow key={keyOf(doc)} doc={doc} accent={accent} onAction={onAction} pinned onTogglePin={togglePin} />
            ))}
          </Card>
        </Section>
      )}
      {groups.map((group) => {
        const items = group.items.filter(isAvailable);
        if (items.length === 0) return null;
        return (
          <Section key={group.id} title={group.title}>
            <Card>
              {items.map((doc) => (
                <DocumentRow key={keyOf(doc)} doc={doc} accent={accent} onAction={onAction} pinned={pinned.includes(keyOf(doc))} onTogglePin={togglePin} />
              ))}
            </Card>
          </Section>
        );
      })}
    </div>
  );
};

export default DocumentList;
